import "./HeaderItem.scss";
import { useRef, useEffect } from "react";
import {
  GridRow,
  GridCell,
  Link,
  ArrowLineUpRightIcon,
  ArrowRightIcon,
  Button,
  IconLookup,
} from "/App.Components";
import { ExpandedMenuProps } from "/Models/Generated/ExpandedMenuProps";
import useClickOutsideWithExcludedClass from "/Helpers/Hooks/useClickOutsideWithExcludedClass.hook";
import { Collapse } from "react-collapse";
import { ColorTheme } from "/Models/Models/ColorTheme";
import { createSpreadableBem } from "/Helpers/createBem";

interface Props extends ExpandedMenuProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const HeaderItem = ({
  menuName,
  introHeading,
  introText,
  overviewLink,
  children,
  isOpen,
  setIsOpen,
  ctaLink,
  ctaIcon,
}: Props) => {
  const toggleExpandedMenuItem = () => {
    setIsOpen(!isOpen);
  };

  const expandedMenuRef = useRef(null);
  const containerRef = useRef<HTMLLIElement>(null);

  const handleClickOutside = () => setIsOpen(false);
  const bem = createSpreadableBem("header-item-norad");

  useClickOutsideWithExcludedClass(
    containerRef,
    handleClickOutside,
    "focusout",
    bem("__button").className,
  );
  useClickOutsideWithExcludedClass(containerRef, handleClickOutside, "click");

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.code === "Escape") {
        isOpen && setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen]);

  return (
    <li ref={containerRef} {...bem("")}>
      <button
        type="button"
        onClick={toggleExpandedMenuItem}
        {...bem("__button", { "--is-open": isOpen })}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>{menuName}</span>
      </button>

      <Collapse
        isOpened={isOpen}
        theme={{ collapse: bem("__expanded-menu-item").className }}
      >
        <div ref={expandedMenuRef} {...bem("__expanded-menu-item-content")}>
          <GridRow>
            <GridCell
              startColsDesktop={1}
              spanColsDesktop={4}
              startColsTablet={1}
              spanColsTablet={6}
              startColsMobile={1}
              spanColsMobile={6}
            >
              <div {...bem("__expanded-menu-item-intro")}>
                {introHeading &&
                  (overviewLink ? (
                    <Link
                      tabIndex={isOpen ? 0 : -1}
                      size="medium"
                      icon={
                        overviewLink.isExternal
                          ? ArrowLineUpRightIcon
                          : ArrowRightIcon
                      }
                      as="h2"
                      iconPlacement="after"
                      text={introHeading}
                      {...overviewLink}
                    />
                  ) : (
                    <h2 {...bem("__expanded-menu-item-heading")}>
                      {introHeading}
                    </h2>
                  ))}
                {introText && (
                  <p {...bem("__expanded-menu-item-text")}>{introText}</p>
                )}
                {ctaLink?.text && (
                  <div {...bem("__expanded-menu-item-intro__cta-wrapper")}>
                    <Button
                      {...(ctaIcon && {
                        iconBefore: (
                          <IconLookup
                            iconType={ctaIcon}
                            iconProps={{
                              thickness: "Reg",
                              size: "32",
                              color: ColorTheme.black,
                            }}
                          />
                        ),
                      })}
                      size="l"
                      style={"primary"}
                      label={ctaLink.text}
                      href={ctaLink.href}
                      tabIndex={isOpen ? 0 : -1}
                    />
                  </div>
                )}
              </div>
            </GridCell>

            <GridCell
              startColsDesktop={5}
              startColsDesktopMediumMin={6}
              spanColsDesktop={7}
              startColsTablet={1}
              spanColsTablet={6}
              startColsMobile={1}
              spanColsMobile={6}
            >
              <div {...bem("__expanded-menu-item-categories")}>
                {children &&
                  children.length !== 0 &&
                  (() => {
                    const splitIndex =
                      children.length > 2 ? 2 : Math.ceil(children.length / 2);
                    const col1 = children.slice(0, splitIndex);
                    const col2 = children.slice(splitIndex);
                    return [col1, col2].map((col, colIndex) => (
                      <div
                        key={colIndex}
                        {...bem("__expanded-menu-item-categories-col")}
                      >
                        {col.map((item, index) => (
                          <div
                            {...bem("__expanded-menu-item-category")}
                            key={(item.category ?? "") + index}
                          >
                            {item?.category && (
                              <h3
                                {...bem("__expanded-menu-item-category-title")}
                              >
                                {item.category}
                              </h3>
                            )}
                            <ul
                              aria-hidden={!isOpen}
                              {...bem("__expanded-menu-item-category-list")}
                            >
                              {item.links &&
                                item.links.length !== 0 &&
                                item.links.map((link, index) => (
                                  <li
                                    key={link.text && link.text + index}
                                    {...bem(
                                      "__expanded-menu-item-category-link",
                                    )}
                                  >
                                    <Link
                                      size="small"
                                      {...link}
                                      icon={
                                        link.isExternal
                                          ? ArrowLineUpRightIcon
                                          : undefined
                                      }
                                      iconSize="20"
                                      iconPlacement="after"
                                      tabIndex={isOpen ? 0 : -1}
                                    />
                                  </li>
                                ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
              </div>
            </GridCell>
          </GridRow>
        </div>
      </Collapse>
    </li>
  );
};

export default HeaderItem;
